// Relances automatiques, « J'ai déjà réglé » et liste « À appeler » :
// règles d'AFFICHAGE partagées par tous les écrans (tableau de bord, fiche de
// la promesse, page des promesses, réglages, page publique de paiement).
//
// Rien ici ne décide d'un paiement : le serveur reste seul juge (une promesse
// ne passe JAMAIS « payée » sans un geste de la synagogue). Ce module ne fait
// que lire les champs renvoyés par l'API et les traduire en phrases simples.
//
// Module PUR (aucun appel réseau, aucun import de lib/api) : testé tel quel
// dans relances.test.js.

// ── Rythme des relances : RÉGLAGE de la synagogue (30/09/2026) ───────────
// Même règles que le serveur (services/reminder_rhythm.py) : si un rythme
// change côté serveur, on ne touche qu'ici. Le lien de paiement envoyé à la
// saisie (J0) compte comme premier contact ; les jours sont comptés depuis
// la saisie.
export const RYTHME_PAR_DEFAUT = "normal";
export const HEURE_PAR_DEFAUT = "10:00";   // heure d'envoi par défaut (heure de Paris)
export const RELANCE_MENSUELLE_JOURS = 30; // « chaque mois »

// `nb` : relances espacées de `ecart` jours APRÈS la 1re, puis 1 par mois ;
// null = jusqu'à « À appeler » (soutenu). `appel` : jours avant « À appeler ».
export const RYTHMES = [
  { cle: "normal", titre: "Normal", recommande: true, premiere: 3, ecart: 14, nb: 2, appel: 90 },
  { cle: "doux", titre: "Doux", premiere: 7, ecart: 30, nb: 0, appel: 90 },
  { cle: "soutenu", titre: "Soutenu", premiere: 3, ecart: 14, nb: null, appel: 90 },
];
export const CLES_RYTHME = ["normal", "doux", "soutenu", "personnalise"];

// Réglage « personnalisé » : bornes (identiques au serveur, qui les
// ré-applique de toute façon) et pas des boutons − / +.
export const BORNES_PERSONNALISE = {
  premiere: { min: 1, max: 30, pas: 1 },
  ecart: { min: 7, max: 60, pas: 1 },
  nb: { min: 0, max: 6, pas: 1 },
  appel: { min: 30, max: 365, pas: 15 },
};
// « À appeler » au moins une semaine après la 1re relance (même règle que le
// serveur) : sinon aucune relance ne partirait, ou un Chabbat suffirait à la
// supprimer. Le curseur « À appeler » suit la 1re relance.
export const ECART_PREMIERE_RELANCE_APPEL_JOURS = 7;

// Bornes des 4 réglages pour des valeurs données : le minimum de
// « À appeler » dépend de la 1re relance.
export function bornesPersonnalise(valeurs) {
  const premiere = Number(valeurs && valeurs.premiere) || BORNES_PERSONNALISE.premiere.min;
  return {
    ...BORNES_PERSONNALISE,
    appel: {
      ...BORNES_PERSONNALISE.appel,
      min: Math.max(BORNES_PERSONNALISE.appel.min, premiere + ECART_PREMIERE_RELANCE_APPEL_JOURS),
    },
  };
}

// Nom des champs échangés avec le serveur (GET / PUT /organizations/{id}).
export const CHAMPS_API_RYTHME = {
  premiere: "reminder_custom_first_days",
  ecart: "reminder_custom_every_days",
  nb: "reminder_custom_count",
  appel: "reminder_custom_call_after_days",
};

const NORMAL = RYTHMES[0];

export function borner(valeur, { min, max }, defaut) {
  const n = typeof valeur === "number" ? valeur : Number(valeur);
  if (valeur === null || valeur === undefined || valeur === "" || typeof valeur === "boolean" || !Number.isFinite(n)) {
    return defaut;
  }
  return Math.max(min, Math.min(max, Math.trunc(n)));
}

// Réglage lu dans la réponse du serveur (ou dans le formulaire) :
// { reminder_rhythm, reminder_custom_first_days, … } toujours complet et
// borné. Rien de réglé (synagogues existantes) → « normal ».
export function reglageRythme(source) {
  const s = source && typeof source === "object" ? source : {};
  const brut = String(s.reminder_rhythm || "").trim().toLowerCase();
  const out = { reminder_rhythm: CLES_RYTHME.includes(brut) ? brut : RYTHME_PAR_DEFAUT };
  Object.entries(CHAMPS_API_RYTHME).forEach(([cle, champ]) => {
    out[champ] = borner(s[champ], BORNES_PERSONNALISE[cle], NORMAL[cle]);
  });
  const miniAppel = out.reminder_custom_first_days + ECART_PREMIERE_RELANCE_APPEL_JOURS;
  if (out.reminder_custom_call_after_days < miniAppel) out.reminder_custom_call_after_days = miniAppel;
  return out;
}

export function memeReglageRythme(a, b) {
  const x = reglageRythme(a);
  const y = reglageRythme(b);
  return Object.keys(x).every((k) => x[k] === y[k]);
}

const CHAMPS_RYTHME = ["reminder_rhythm", ...Object.values(CHAMPS_API_RYTHME)];

// Enregistrement de la page Réglages (qui renvoie tout le formulaire) : le
// rythme n'est envoyé que s'il a CHANGÉ. Sinon, enregistrer l'onglet
// « Informations » écrirait « normal » en base pour une synagogue qui n'a
// jamais rien choisi — elle ne suivrait plus le défaut recommandé s'il
// change un jour (même piège que l'ancien « vendredi 13 h »).
export function retirerRythmeSiInchange(payload, org) {
  if (!memeReglageRythme(payload, org)) return payload;
  const out = { ...payload };
  CHAMPS_RYTHME.forEach((k) => { delete out[k]; });
  return out;
}

// Les champs du rythme à envoyer au serveur (aperçu, enregistrement).
export function champsRythme(source) {
  const r = reglageRythme(source);
  return Object.fromEntries(CHAMPS_RYTHME.map((k) => [k, r[k]]));
}

// Le rythme appliqué : { cle, premiere, ecart, nb, appel }.
export function rythmeDepuisReglage(source) {
  const r = reglageRythme(source);
  const preset = RYTHMES.find((x) => x.cle === r.reminder_rhythm);
  if (preset) return preset;
  return {
    cle: "personnalise",
    titre: "Personnalisé",
    premiere: r.reminder_custom_first_days,
    ecart: r.reminder_custom_every_days,
    nb: r.reminder_custom_count,
    appel: r.reminder_custom_call_after_days,
  };
}

// Jours (comptés depuis la saisie) où partent les relances automatiques,
// avant « À appeler » (une relance le jour de la bascule ou après ne part
// pas — même règle que le serveur). Hors reports de Chabbat / fêtes.
export function joursDesRelances(r) {
  const jours = [];
  let jour = r.premiere;
  let faites = 0;
  while (jour < r.appel && jours.length < 400) {
    jours.push(jour);
    faites += 1;
    const sansFin = r.nb === null || r.nb === undefined;
    jour += sansFin || faites <= r.nb ? r.ecart : RELANCE_MENSUELLE_JOURS;
  }
  return jours;
}

const nbJours = (n) => (n === 1 ? "1 jour" : `${n} jours`);

// « toutes les 2 semaines », « chaque semaine », « tous les 10 jours ».
function tousLes(n) {
  if (n === 7) return "chaque semaine";
  if (n % 7 === 0) return `toutes les ${n / 7} semaines`;
  return `tous les ${n} jours`;
}

// Délai avant « À appeler » : « 3 mois », « 1 an », « 45 jours ».
export function dureeAvantAppel(n) {
  if (n === 365) return "1 an";
  if (n % 30 === 0) return `${n / 30} mois`;
  return nbJours(n);
}

// Étapes de la frise : « 3 jours après · puis 14 jours · puis 14 jours ·
// puis chaque mois · à 3 mois : à appeler ». Calculées sur les VRAIS jours
// d'envoi : une étape qui tomberait après « À appeler » n'est pas affichée.
export function etapesFrise(r) {
  const liste = joursDesRelances(r);
  const etapes = [];
  if (!liste.length) {
    etapes.push({ texte: "aucune relance automatique" });
  } else {
    etapes.push({ texte: `${nbJours(r.premiere)} après` });
    const sansFin = r.nb === null || r.nb === undefined;
    const rapprochees = sansFin ? liste.slice(1) : liste.slice(1, 1 + r.nb);
    if (rapprochees.length && sansFin) {
      etapes.push({ texte: `puis ${tousLes(r.ecart)}` });
    } else if (rapprochees.length > 2) {
      etapes.push({ texte: `puis ${rapprochees.length} fois, ${tousLes(r.ecart)}` });
    } else {
      rapprochees.forEach(() => etapes.push({ texte: `puis ${nbJours(r.ecart)}` }));
    }
    if (!sansFin && liste.length > 1 + r.nb) etapes.push({ texte: "puis chaque mois" });
  }
  etapes.push({ texte: `à ${dureeAvantAppel(r.appel)} : à appeler`, appel: true });
  return etapes;
}

export function texteFrise(r) {
  return etapesFrise(r).map((e) => e.texte).join(" · ");
}

// « 4 relances au plus » — sous le titre de chaque carte.
export function nombreRelances(r) {
  const n = joursDesRelances(r).length;
  if (n === 0) return "Aucune relance";
  return n === 1 ? "1 relance au plus" : `${n} relances au plus`;
}

// Phrase d'explication du rythme choisi (écran de réglage).
export function texteRythme(r = NORMAL) {
  const n = joursDesRelances(r).length;
  const relances = n === 0
    ? "aucune relance automatique ne part"
    : `le donateur reçoit au plus ${n === 1 ? "1 relance" : `${n} relances`}`;
  return (
    `Le lien de paiement part dès la saisie de la promesse. S'il n'a pas payé, ${relances} ; ` +
    `au bout de ${dureeAvantAppel(r.appel)}, la promesse passe dans votre liste « À appeler ».`
  );
}

// Ce que le nouveau rythme changerait au prochain envoi, lu dans la réponse
// de POST /organizations/{id}/reminders/rhythm-preview :
// { prochain_envoi, heure, actuel: { donateurs_relances, a_appeler }, propose: {…} }.
// null si rien ne change (ou réponse illisible).
export function phraseApercuRythme(apercu) {
  const a = apercu && apercu.actuel;
  const p = apercu && apercu.propose;
  if (!a || !p) return null;
  const n = (x) => Math.max(0, Number(x) || 0);
  const [dA, dP, aA, aP] = [n(a.donateurs_relances), n(p.donateurs_relances), n(a.a_appeler), n(p.a_appeler)];
  const morceaux = [];
  if (dP !== dA) {
    const qui = dP === 0 ? "aucun donateur ne recevra de relance"
      : dP === 1 ? "1 donateur recevra une relance"
        : `${dP} donateurs recevront une relance`;
    morceaux.push(`${qui} (${dA} avec le rythme actuel)`);
  }
  if (aP !== aA) {
    const combien = aP === 1 ? "1 promesse passera" : `${aP} promesses passeront`;
    morceaux.push(`${combien} dans votre liste «\u00a0À appeler\u00a0» (${aA} avec le rythme actuel)`);
  }
  if (!morceaux.length) return null;
  const jour = dateLongue(apercu.prochain_envoi);
  const heure = String(apercu.heure || "").replace(/^0?(\d{1,2}):00$/, "$1\u00a0h").replace(/^0?(\d{1,2}):(\d{2})$/, "$1\u00a0h\u00a0$2");
  const quand = jour ? ` (${jour}${heure ? ` à ${heure}` : ""})` : "";
  return `Au prochain envoi${quand}, ${morceaux.join(" et ")}.`;
}

// Repli quand l'aperçu chiffré n'est pas disponible : l'effet concret d'un
// « À appeler » raccourci.
export function phraseAAppelerRaccourci(avant, apres) {
  const x = rythmeDepuisReglage(avant);
  const y = rythmeDepuisReglage(apres);
  if (y.appel >= x.appel) return null;
  return `Les promesses impayées depuis plus de ${dureeAvantAppel(y.appel)} passeront dans votre liste «\u00a0À appeler\u00a0» dès le prochain envoi, et ne seront plus relancées automatiquement.`;
}

export const TEXTE_JOURS_SANS_ENVOI = "Jamais pendant Chabbat ni les jours de fête.";
export const DETAIL_JOURS_SANS_ENVOI =
  "Une relance prévue le vendredi après-midi, le samedi, un jour de fête (Roch Hachana, Kippour, " +
  "Souccot, Pessah, Chavouot…) ou la veille d'une fête après midi part le jour permis suivant.";

// Sous le réglage de l'heure : à partir de midi, le passage du vendredi (et
// d'une veille de fête) n'a jamais lieu — le responsable doit le savoir.
export const TEXTE_HEURE_APRES_MIDI =
  "Après 12 h, les relances qui tombent un vendredi ou une veille de fête partent le jour permis suivant.";

export function heureApresMidi(heure) {
  const h = Number(String(heure || "").split(":")[0]);
  return Number.isFinite(h) && h >= 12;
}

// Heure réellement utilisée par le serveur (même règle que
// services/reminder_rhythm.heure_envoi) : l'ANCIEN écran enregistrait
// « vendredi 13 h, chaque semaine » à chaque sauvegarde, sans que personne ne
// l'ait choisi — sans le repère `heure_validee` (posé quand le responsable
// touche l'heure dans le nouvel écran), ce trio vaut 10 h.
export function heureEffective(rt) {
  const r = rt && typeof rt === "object" ? rt : {};
  const brut = String(r.reminder_time || r.friday_time || "").trim();
  const m = /^(\d{1,2}):(\d{2})/.exec(brut);
  if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) return HEURE_PAR_DEFAUT;
  const heure = `${m[1].padStart(2, "0")}:${m[2]}`;
  const ancienDefaut = !r.heure_validee
    && heure === "13:00"
    && (r.reminder_day || "friday") === "friday"
    && (r.reminder_frequency || "weekly") === "weekly";
  return ancienDefaut ? HEURE_PAR_DEFAUT : heure;
}

// ── Déclaration « J'ai déjà réglé » ────────────────────────────────────────
// Codes échangés avec le serveur (ne JAMAIS les traduire : ce sont des valeurs
// d'API) → libellés affichés.
export const METHODES_DECLARATION = [
  { value: "especes", label: "Espèces" },
  { value: "cheque", label: "Chèque" },
  { value: "virement", label: "Virement" },
  { value: "autre", label: "Autre" },
];

export function libelleMethode(code) {
  const m = METHODES_DECLARATION.find((x) => x.value === code);
  return m ? m.label : "Autre";
}

const ETATS = ["pending", "confirmed", "refused"];

// État d'une déclaration : "pending" | "confirmed" | "refused" | null.
// Toute valeur inconnue est traitée comme « pas de déclaration » (prudence).
export function etatDeclaration(declaration) {
  const s = declaration && declaration.status;
  return ETATS.includes(s) ? s : null;
}

export const declarationEnAttente = (declaration) => etatDeclaration(declaration) === "pending";

// Champs « relances » d'une promesse, quelle que soit la forme reçue :
//  - élément de liste (GET /pledges) : champs à plat ;
//  - fiche (GET /pledges/{id}) : { pledge: {...}, donor, ... } — les champs
//    peuvent être dans `pledge` ou à la racine selon le serveur.
export function champsRelance(source) {
  const s = source || {};
  const p = s.pledge && typeof s.pledge === "object" ? s.pledge : {};
  const lire = (cle) => (p[cle] !== undefined && p[cle] !== null ? p[cle] : s[cle]);
  return {
    declaration: lire("donor_declaration") || null,
    aAppeler: Boolean(lire("to_call")),
    aAppelerDepuis: lire("to_call_since") || null,
    prochaineRelance: lire("next_reminder_at") || null,
  };
}

// Ce que la fiche de la promesse doit montrer au responsable, par priorité :
//  1. "closed"    : payée ou annulée → plus rien à suivre ;
//  2. "declared"  : le donateur dit avoir réglé → décision à prendre ;
//  3. "to_call"   : fin des relances sans paiement → appeler de vive voix ;
//  4. "scheduled" : prochaine relance automatique connue ;
//  5. "none"      : aucune relance automatique prévue.
export function etatRelances(source, statut) {
  const c = champsRelance(source);
  if (statut === "paid" || statut === "cancelled") return { kind: "closed", ...c };
  if (declarationEnAttente(c.declaration)) return { kind: "declared", ...c };
  if (c.aAppeler) return { kind: "to_call", ...c };
  if (c.prochaineRelance) return { kind: "scheduled", ...c };
  return { kind: "none", ...c };
}

// Décision demandée par le lien de l'email envoyé au responsable :
// /pledges/{id}?declaration=confirm  ou  ?declaration=refuse
// (on accepte aussi ?decision=… et quelques synonymes français, par prudence).
export function decisionDepuisUrl(search) {
  let params;
  try {
    params = new URLSearchParams(search || "");
  } catch {
    return null;
  }
  const brut = (params.get("declaration") || params.get("decision") || "").toLowerCase();
  if (["confirm", "confirmer", "oui"].includes(brut)) return "confirm";
  if (["refuse", "refuser", "non", "pas-recu"].includes(brut)) return "refuse";
  if (brut) return "open";
  return null;
}

// ── Formats d'affichage ────────────────────────────────────────────────────
export function formatEuros(v) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(v || 0));
}

// "YYYY-MM-DD" lu SANS conversion de fuseau (sinon le 12/10 peut devenir le
// 11/10 selon l'appareil) ; une date ISO complète est lue normalement.
function lireDate(valeur) {
  if (!valeur) return null;
  if (valeur instanceof Date) return Number.isNaN(valeur.getTime()) ? null : valeur;
  const s = String(valeur);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

// « 12/10 » (jour/mois) — l'année n'est ajoutée que si elle diffère.
export function dateCourte(valeur, maintenant = new Date()) {
  const d = lireDate(valeur);
  if (!d) return "";
  const jj = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return d.getFullYear() === maintenant.getFullYear() ? `${jj}/${mm}` : `${jj}/${mm}/${d.getFullYear()}`;
}

// « dimanche 12 octobre » — pour la prochaine relance.
export function dateLongue(valeur) {
  const d = lireDate(valeur);
  if (!d) return "";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}

// Ancienneté lisible : « aujourd'hui », « 12 jours », « 3 mois », « 1 an ».
export function anciennete(valeur, maintenant = new Date()) {
  const d = lireDate(valeur);
  if (!d) return "";
  const jours = Math.floor((maintenant.getTime() - d.getTime()) / 86400000);
  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return "1 jour";
  if (jours < 31) return `${jours} jours`;
  if (jours < 365) return `${Math.floor(jours / 30)} mois`;
  const ans = Math.floor(jours / 365);
  return ans === 1 ? "1 an" : `${ans} ans`;
}

// Lien d'appel cliquable (tel:) : on garde le « + » initial et les chiffres ;
// le « (0) » des numéros internationaux (+33 (0)6…) ne se compose pas.
export function lienTelephone(tel) {
  if (!tel) return null;
  const brut = String(tel).trim();
  const chiffres = (brut.startsWith("+") ? brut.replace(/\(0\)/g, "") : brut).replace(/[^\d]/g, "");
  if (chiffres.length < 6) return null;
  return `tel:${brut.startsWith("+") ? "+" : ""}${chiffres}`;
}

// « espèces, le 12/10 » — détail court d'une déclaration.
export function detailDeclaration(declaration, maintenant = new Date()) {
  if (!declaration) return "";
  const moyen = libelleMethode(declaration.method).toLowerCase();
  const date = dateCourte(declaration.paid_on, maintenant);
  return date ? `${moyen}, le ${date}` : moyen;
}

// Montant à vérifier : ce qui RESTE DÛ (promesse payée en partie : le
// gabbaï cherche 80 € dans la caisse, pas 180 €). Repli : montant promis.
export function montantAVerifier(item) {
  const du = item && item.amount_due;
  return typeof du === "number" ? du : Number((item && item.amount) || 0);
}

// Montant promis, à citer en second plan quand une partie est déjà payée
// (« sur 180 € ») ; null sinon.
export function montantPromisSiPartiel(item) {
  const total = Number((item && item.amount) || 0);
  return total - montantAVerifier(item) > 0.01 ? total : null;
}

// « David Cohen dit avoir réglé 180 € (espèces, le 12/10) » — même phrase
// que l'email envoyé au responsable (reste dû).
export function phraseDeclaration(item, maintenant = new Date()) {
  const nom = (item && item.donor_name) || "Un donateur";
  const detail = detailDeclaration(item, maintenant);
  return `${nom} dit avoir réglé ${formatEuros(montantAVerifier(item))}${detail ? ` (${detail})` : ""}`;
}

// Promesse en plusieurs fois (Beraha 12 × 100 €) : le donateur qui dit
// avoir réglé a peut-être payé UNE échéance. On propose alors au gabbaï de
// confirmer l'échéance seule (sinon « Confirmer » solderait tout le reste).
//  - ligne du bandeau : champs `installment_amount` / `installments_left` ;
//  - fiche (GET /pledges/{id}) : `installments` + `amount_due`.
// Renvoie { montant, restantes } ou null.
export function echeanceConfirmable(source) {
  const s = source || {};
  if (typeof s.installment_amount === "number" && s.installment_amount > 0) {
    return { montant: s.installment_amount, restantes: Number(s.installments_left || 0) };
  }
  const p = s.pledge && typeof s.pledge === "object" ? s.pledge : null;
  if (!p || Number(p.installments_total || 1) <= 1 || !Array.isArray(s.installments)) return null;
  const aPayer = s.installments
    .filter((i) => i && i.status === "pending")
    .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0));
  const du = Number(s.amount_due || 0);
  const montant = aPayer.length ? Math.min(Number(aPayer[0].amount || 0), du) : 0;
  return montant > 0 && montant < du - 0.01 ? { montant, restantes: aPayer.length } : null;
}

// Nombre de PERSONNES dans une liste (une ligne par promesse côté serveur :
// une commande de fête de 3 lignes = 1 donateur, pas 3).
export function compterDonateurs(items) {
  const cles = new Set((items || []).map((x) => x.donor_id || `promesse:${x.pledge_id}`));
  return cles.size;
}

// « 2 donateurs disent avoir réglé » / « 1 donateur dit avoir réglé (3 promesses) ».
export function titreBandeauDeclarations(nbDonateurs, nbPromesses = nbDonateurs) {
  const base = nbDonateurs > 1 ? `${nbDonateurs} donateurs disent avoir réglé` : "1 donateur dit avoir réglé";
  return nbPromesses > nbDonateurs ? `${base} (${nbPromesses} promesses)` : base;
}

// Après « Pas reçu » : ce qui va VRAIMENT se passer, lu sur la promesse
// renvoyée par le serveur (jamais une promesse générique « dans quelques
// jours » : la promesse peut repartir dans « À appeler », ou le donateur
// être exclu des relances).
export function phraseApresRefus(reponse) {
  const c = champsRelance(reponse);
  if (c.aAppeler) return "Elle retourne dans votre liste «\u00a0À appeler\u00a0».";
  if (c.prochaineRelance) return `Prochaine relance automatique\u00a0: ${dateLongue(c.prochaineRelance)}.`;
  return "Aucune relance automatique n'est prévue pour l'instant.";
}

// Liste « À appeler » regroupée par DONATEUR : le gabbaï appelle une
// personne, pas une promesse. Ordre : le plus ancien retard d'abord.
// Chaque groupe : { cle, donor_id, donor_name, phone, email, total,
// promesses[], plusAncienne, derniereRelance, relances }.
export function grouperAAppeler(items) {
  const groupes = new Map();
  (items || []).forEach((it) => {
    const cle = it.donor_id || `promesse:${it.pledge_id}`;
    if (!groupes.has(cle)) {
      groupes.set(cle, {
        cle,
        donor_id: it.donor_id || null,
        donor_name: it.donor_name,
        phone: it.phone || null,
        email: it.email || null,
        total: 0,
        promesses: [],
        plusAncienne: null,
        derniereRelance: null,
        relances: 0,
      });
    }
    const g = groupes.get(cle);
    g.promesses.push(it);
    g.total += montantAVerifier(it);
    g.phone = g.phone || it.phone || null;
    g.email = g.email || it.email || null;
    if (it.created_at && (!g.plusAncienne || String(it.created_at) < String(g.plusAncienne))) g.plusAncienne = it.created_at;
    if (it.last_reminder_at && (!g.derniereRelance || String(it.last_reminder_at) > String(g.derniereRelance))) {
      g.derniereRelance = it.last_reminder_at;
    }
    g.relances = Math.max(g.relances, Number(it.reminders_count || 0));
  });
  return Array.from(groupes.values()).sort((a, b) => String(a.plusAncienne || "").localeCompare(String(b.plusAncienne || "")));
}

// Date du jour au format des champs <input type="date"> (heure locale).
export function aujourdhuiISO(maintenant = new Date()) {
  const d = maintenant;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
